// src/Services/InstructorService/InstructorCourseManagementService.ts
import httpClient from '@/services/httpClient';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Standard API response wrapper from backend
 * All API endpoints return this structure: { success, data, message }
 */
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

/**
 * Course creation/update request payload
 */
export interface CoursePayload {
  id?: number;
  creator_id?: string;
  title: string;
  instructor?: string;
  shortDescription?: string;
  description?: string;
  languageId: number;
  categoryId: number;
  subCategoryId: number;
  level: 'beginner' | 'intermediate' | 'advanced';
  courseDuration?: string;
  sections?: Section[];
  thumbnail?: string | File;
  mediaType?: string;
  mediaUrl?: string | File;
  metaKeywords?: string[];
  metaDescription?: string;
  outcomes?: string[];
  requirements?: string[];
  faqs?: FAQ[];
  lessons?: Lesson[];
  batchIds?: number[];
  availableToAllBatches?: boolean;
  status?: 'pending' | 'active' | 'inactive';
}

/**
 * Course section
 */
export interface Section {
  id?: number;
  title: string;
  sectionOrder?: number;
  createdAt?: string;
  lessons?: Lesson[];
}

/**
 * Course lesson
 */
export interface Lesson {
  id?: number | string;
  title: string;
  section: string;
  sectionId?: number;
  lessonType: 'ILTS' | 'Content-Based';
  skills?: string[];

  // Content-Based fields
  contentType?: 'document' | 'scorm' | 'mp4' | 'url' | 'quiz';
  lessonContentDocument?: string | File;
  scormPackage?: string | File;
  videoUpload?: string | File;
  contentUrl?: string;
  lessonDuration?: string;
  description?: string;

  // Quiz/Assessment fields
  assessmentId?: number;
  requireSectionCompletion?: boolean;
  assessmentStartDate?: string;
  assessmentEndDate?: string;

  // ILTS fields
  iltsType?: 'Online' | 'Offline';
  iltsUrl?: string;
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  eventVenue?: string;
  meetUrl?: string;
}

/**
 * Course FAQ
 */
export interface FAQ {
  question: string;
  answer: string;
}

/**
 * Course metadata (categories, languages)
 */
export interface Category {
  id: number;
  name: string;
  description?: string;
}

export interface SubCategory {
  id: number;
  name: string;
  categoryId: number;
}

export interface Language {
  id: number;
  name: string;
  code: string;
}

export interface Batch {
  id: number;
  batch_name: string;
}

/**
 * Course details response
 */
export interface CourseDetails {
  id: number;
  creator_id: string;
  title: string;

  // Instructor (API can return either format)
  instructor?: string;
  instructor_name?: string;

  // Description fields (API uses snake_case, code uses camelCase)
  shortDescription?: string;
  short_description?: string;
  description?: string;

  // IDs (API uses snake_case, code uses camelCase)
  languageId?: number;
  language_id?: number;
  languageName?: string;
  language_name?: string;

  categoryId?: number;
  category_id?: number;
  categoryName?: string;
  category_name?: string;

  subCategoryId?: number;
  sub_category_id?: number;
  subCategoryName?: string;
  subcategory_name?: string;

  // Other fields
  level: string;
  courseDuration?: string;
  course_duration?: string;

  thumbnail?: string;
  mediaType?: string;
  mediaUrl?: string;

  // Meta fields (API uses snake_case)
  metaKeywords?: string;
  meta_keywords?: string;
  metaDescription?: string;
  meta_description?: string;

  status: string;
  createdAt?: string;
  created_at?: string;
  updatedAt?: string;
  last_updated?: string;

  enrollmentCount?: number;
  lessonCount?: number;
  total_lessons?: number;

  sections?: Section[];
  lessons?: Lesson[];

  // Outcomes and requirements (API uses "content" field, not "outcomeText"/"requirementText")
  outcomes?: Array<{ id: number; outcomeText?: string; content?: string }>;
  requirements?: Array<{ id: number; requirementText?: string; content?: string }>;
  faqs?: Array<{ id: number; question: string; answer: string }>;

  batches?: any[];
  batchIds?: number[];
  availableToAllBatches?: boolean;
  available_to_all_batches?: number;
}

/**
 * Course list item
 */
export interface CourseListItem {
  id: number;
  title: string;
  shortDescription?: string;
  thumbnail?: string;
  level: string;
  status: string;
  enrollmentCount: number;
  lessonCount: number;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// API ENDPOINTS
// ============================================================================

const ENDPOINTS = {
  // Course CRUD
  CREATE_COURSE: '/instructor/courses',
  UPDATE_COURSE: (courseId: number) => `/instructor/courses/${courseId}`,
  DELETE_COURSE: (courseId: number) => `/instructor/courses/${courseId}`,
  GET_COURSE_DETAILS: (courseId: number) => `/instructor/courses/${courseId}`,

  // Course updates (specific sections)
  UPDATE_BASIC_INFO: (courseId: number) => `/instructor/courses/${courseId}/basic`,
  UPDATE_DETAILS: (courseId: number) => `/instructor/courses/${courseId}/details`,
  UPDATE_MEDIA: (courseId: number) => `/instructor/courses/${courseId}/media`,
  UPDATE_META: (courseId: number) => `/instructor/courses/${courseId}/meta`,

  // Sections and Lessons
  ADD_SECTION: (courseId: number) => `/instructor/courses/${courseId}/sections`,
  GET_SECTIONS: (courseId: number) => `/instructor/courses/${courseId}/sections`,
  ADD_LESSON: (courseId: number) => `/instructor/courses/${courseId}/lessons`,
  UPDATE_LESSON: (courseId: number, lessonId: number) => `/instructor/courses/${courseId}/lessons/${lessonId}`,
  DELETE_LESSON: (courseId: number, lessonId: number) => `/instructor/courses/${courseId}/lessons/${lessonId}`,
  REORDER_LESSONS: (courseId: number) => `/instructor/courses/${courseId}/lessons/reorder`,

  // Outcomes, Requirements, FAQs
  UPDATE_OUTCOMES: (courseId: number) => `/instructor/courses/${courseId}/outcomes`,
  UPDATE_REQUIREMENTS: (courseId: number) => `/instructor/courses/${courseId}/requirements`,
  UPDATE_FAQS: (courseId: number) => `/instructor/courses/${courseId}/faqs`,

  // Course lists
  GET_ACTIVE_COURSES: '/instructor/courses/instructor/active',
  GET_PENDING_COURSES: '/instructor/courses/instructor/pending',

  // Metadata
  GET_CATEGORIES: '/instructor/metadata/categories',
  GET_SUBCATEGORIES: '/instructor/metadata/subcategories',
  GET_LANGUAGES: '/instructor/metadata/languages',
  ADD_CATEGORY: '/instructor/metadata/categories',
  ADD_SUBCATEGORY: '/instructor/metadata/subcategories',
  GET_BATCHES: '/instructor/batches',

  // Enrollments and Analytics
  GET_ENROLLED_STUDENTS: (courseId: number) => `/instructor/courses/${courseId}/students`,
  GET_STUDENT_PROGRESS: (courseId: number) => `/instructor/courses/${courseId}/enrollments/progress`,
  GET_COURSE_ANALYTICS: (courseId: number) => `/instructor/courses/${courseId}/analytics`,
  GET_COURSE_BATCHES: (courseId: number) => `/instructor/courses/${courseId}/batches`,
};

// ============================================================================
// SERVICE FUNCTIONS - Course CRUD
// ============================================================================

/**
 * Create a new course
 * @param courseData - Course creation data
 */
export const createCourse = async (courseData: CoursePayload): Promise<{
  success: boolean;
  data?: { courseId: number; id: number };
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.post<ApiResponse<{ courseId: number; id: number }>>(
      ENDPOINTS.CREATE_COURSE,
      courseData
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to create course',
      },
    };
  }
};

/**
 * Update an existing course
 * @param courseId - ID of the course to update
 * @param courseData - Updated course data
 */
export const updateCourse = async (
  courseId: number,
  courseData: Partial<CoursePayload>
): Promise<{
  success: boolean;
  data?: any;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.put<ApiResponse<any>>(
      ENDPOINTS.UPDATE_COURSE(courseId),
      courseData
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to update course',
      },
    };
  }
};

/**
 * Delete a course (soft delete)
 * @param courseId - ID of the course to delete
 */
export const deleteCourse = async (courseId: number): Promise<{
  success: boolean;
  error?: { message: string };
}> => {
  try {
    await httpClient.delete<ApiResponse<null>>(ENDPOINTS.DELETE_COURSE(courseId));

    return {
      success: true,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to delete course',
      },
    };
  }
};

/**
 * Get course details by ID
 * @param courseId - ID of the course
 */
export const getCourseDetails = async (courseId: number): Promise<{
  success: boolean;
  data?: CourseDetails;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.get<ApiResponse<CourseDetails>>(
      ENDPOINTS.GET_COURSE_DETAILS(courseId)
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch course details',
      },
    };
  }
};

// ============================================================================
// SERVICE FUNCTIONS - Course Updates (Specific Sections)
// ============================================================================

/**
 * Update course basic information
 */
export const updateCourseBasicInfo = async (
  courseId: number,
  data: {
    title?: string;
    shortDescription?: string;
    description?: string;
    category?: number;
    subcategory?: number;
    level?: string;
    language?: number;
    courseDuration?: string;
    batchIds?: number[];
    availableToAllBatches?: boolean;
  }
): Promise<{
  success: boolean;
  data?: any;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.put<ApiResponse<any>>(
      ENDPOINTS.UPDATE_BASIC_INFO(courseId),
      data
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to update basic info',
      },
    };
  }
};

/**
 * Update course details (outcomes, requirements, FAQs)
 */
export const updateCourseDetails = async (
  courseId: number,
  data: {
    outcomes?: string[];
    requirements?: string[];
    faqs?: FAQ[];
  }
): Promise<{
  success: boolean;
  data?: any;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.put<ApiResponse<any>>(
      ENDPOINTS.UPDATE_DETAILS(courseId),
      data
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to update course details',
      },
    };
  }
};

/**
 * Update course media (thumbnail, overview video)
 */
export const updateCourseMedia = async (
  courseId: number,
  data: {
    mediaType?: string;
    mediaUrl?: string | File;
  }
): Promise<{
  success: boolean;
  data?: any;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.put<ApiResponse<any>>(
      ENDPOINTS.UPDATE_MEDIA(courseId),
      data
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to update course media',
      },
    };
  }
};

// ============================================================================
// SERVICE FUNCTIONS - Sections and Lessons
// ============================================================================

/**
 * Add a section to a course
 */
export const addSection = async (
  courseId: number,
  sectionTitle: string
): Promise<{
  success: boolean;
  data?: { sectionId: number };
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.post<ApiResponse<{ sectionId: number }>>(
      ENDPOINTS.ADD_SECTION(courseId),
      { title: sectionTitle }
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to add section',
      },
    };
  }
};

/**
 * Get sections for a course
 */
export const getSections = async (courseId: number): Promise<{
  success: boolean;
  data?: Section[];
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.get<ApiResponse<Section[]>>(
      ENDPOINTS.GET_SECTIONS(courseId)
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch sections',
      },
    };
  }
};

/**
 * Add a lesson to a course
 */
export const addLesson = async (
  courseId: number,
  lessonData: Lesson
): Promise<{
  success: boolean;
  data?: { id: number };
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.post<ApiResponse<{ id: number }>>(
      ENDPOINTS.ADD_LESSON(courseId),
      lessonData
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to add lesson',
      },
    };
  }
};

/**
 * Update a lesson
 */
export const updateLesson = async (
  courseId: number,
  lessonId: number,
  lessonData: Partial<Lesson>
): Promise<{
  success: boolean;
  data?: any;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.put<ApiResponse<any>>(
      ENDPOINTS.UPDATE_LESSON(courseId, lessonId),
      lessonData
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to update lesson',
      },
    };
  }
};

/**
 * Delete a lesson
 */
export const deleteLesson = async (
  courseId: number,
  lessonId: number
): Promise<{
  success: boolean;
  error?: { message: string };
}> => {
  try {
    await httpClient.delete<ApiResponse<null>>(
      ENDPOINTS.DELETE_LESSON(courseId, lessonId)
    );

    return {
      success: true,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to delete lesson',
      },
    };
  }
};

/**
 * Reorder lessons within a section
 */
export const reorderLessons = async (
  courseId: number,
  sectionId: number,
  lessonOrders: Array<{ lessonId: number; lessonOrder: number }>
): Promise<{
  success: boolean;
  error?: { message: string };
}> => {
  try {
    await httpClient.put<ApiResponse<any>>(
      ENDPOINTS.REORDER_LESSONS(courseId),
      { sectionId, lessonOrders }
    );

    return { success: true };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to reorder lessons',
      },
    };
  }
};

// ============================================================================
// SERVICE FUNCTIONS - Course Lists
// ============================================================================

/**
 * Get instructor's active (published) courses
 */
export const getActiveCourses = async (): Promise<{
  success: boolean;
  data?: CourseListItem[];
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.get<ApiResponse<CourseListItem[]>>(
      ENDPOINTS.GET_ACTIVE_COURSES
    );

    return {
      success: true,
      data: response.data || [],
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch active courses',
      },
    };
  }
};

/**
 * Get instructor's pending (draft) courses
 */
export const getPendingCourses = async (): Promise<{
  success: boolean;
  data?: CourseListItem[];
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.get<ApiResponse<CourseListItem[]>>(
      ENDPOINTS.GET_PENDING_COURSES
    );

    return {
      success: true,
      data: response.data || [],
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch pending courses',
      },
    };
  }
};

// ============================================================================
// SERVICE FUNCTIONS - Metadata
// ============================================================================

/**
 * Get all course categories
 */
export const getCategories = async (): Promise<{
  success: boolean;
  data?: Category[];
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.get<ApiResponse<Category[]>>(
      ENDPOINTS.GET_CATEGORIES
    );

    return {
      success: true,
      data: response.data || [],
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch categories',
      },
    };
  }
};

/**
 * Get all course subcategories
 */
export const getSubCategories = async (): Promise<{
  success: boolean;
  data?: SubCategory[];
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.get<ApiResponse<SubCategory[]>>(
      ENDPOINTS.GET_SUBCATEGORIES
    );

    return {
      success: true,
      data: response.data || [],
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch subcategories',
      },
    };
  }
};

/**
 * Get all languages
 */
export const getLanguages = async (): Promise<{
  success: boolean;
  data?: Language[];
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.get<ApiResponse<Language[]>>(
      ENDPOINTS.GET_LANGUAGES
    );

    return {
      success: true,
      data: response.data || [],
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch languages',
      },
    };
  }
};

/**
 * Add a new category
 */
export const addCategory = async (categoryName: string): Promise<{
  success: boolean;
  data?: Category;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.post<ApiResponse<Category>>(
      ENDPOINTS.ADD_CATEGORY,
      { categoryName }
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to add category',
      },
    };
  }
};

/**
 * Add a new subcategory
 */
export const addSubCategory = async (
  categoryId: number,
  subcategoryName: string
): Promise<{
  success: boolean;
  data?: SubCategory;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.post<ApiResponse<SubCategory>>(
      ENDPOINTS.ADD_SUBCATEGORY,
      { categoryId, subcategoryName }
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to add subcategory',
      },
    };
  }
};

/**
 * Get all batches
 */
export const getBatches = async (): Promise<{
  success: boolean;
  data?: Batch[];
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.get<ApiResponse<Batch[]>>(
      ENDPOINTS.GET_BATCHES
    );

    return {
      success: true,
      data: response.data || [],
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch batches',
      },
    };
  }
};

// ============================================================================
// SERVICE FUNCTIONS - Enrollments and Analytics
// ============================================================================

/**
 * Get enrolled students for a course
 */
export const getEnrolledStudents = async (courseId: number): Promise<{
  success: boolean;
  data?: any[];
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.get<ApiResponse<any[]>>(
      ENDPOINTS.GET_ENROLLED_STUDENTS(courseId)
    );

    return {
      success: true,
      data: response.data || [],
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch enrolled students',
      },
    };
  }
};

/**
 * Get student progress for a course
 */
export const getStudentProgress = async (courseId: number): Promise<{
  success: boolean;
  data?: any;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.get<ApiResponse<any>>(
      ENDPOINTS.GET_STUDENT_PROGRESS(courseId)
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch student progress',
      },
    };
  }
};

/**
 * Get course analytics
 */
export const getCourseAnalytics = async (
  courseId: number,
  timeFilter?: '7days' | '30days' | '6months' | 'all'
): Promise<{
  success: boolean;
  data?: any;
  error?: { message: string };
}> => {
  try {
    const url = timeFilter
      ? `${ENDPOINTS.GET_COURSE_ANALYTICS(courseId)}?timeFilter=${timeFilter}`
      : ENDPOINTS.GET_COURSE_ANALYTICS(courseId);

    const response = await httpClient.get<ApiResponse<any>>(url);

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch course analytics',
      },
    };
  }
};

// ============================================================================
// COURSE OFFERINGS & SESSIONS
// ============================================================================

/**
 * Course offering interface
 */
export interface CourseOffering {
  id?: number;
  courseId?: number;
  name: string;
  locationId?: number | null;
  locationName?: string | null;
  location?: string;
  startDate: string;
  endDate: string;
  timezone?: string;
  totalSeats: number;
  availableSeats?: number;
  enableWaitlist: boolean;
  status?: 'Draft' | 'Published' | 'In Progress' | 'Completed' | 'Cancelled';
  sessionCount?: number;
  createdAt?: string;
}

/**
 * Course session interface
 */
export interface CourseSession {
  id?: number;
  courseId?: number;
  offeringId?: number | null;
  sectionId?: number | null;
  lessonId?: number | null;
  name: string;
  sessionDate: string;
  startTime: string;
  endTime: string;
  instructorId?: string | null;
  instructorName?: string;
  deliveryMethod: 'In-Person' | 'Virtual' | 'Hybrid';
  locationId?: number | null;
  roomId?: number | null;
  location?: string;
  roomName?: string;
  locationRoom?: string | null;
  virtualMeetingLink?: string | null;
  maxCapacity?: number;
  enrolledCount?: number;
  description?: string | null;
  status?: 'Scheduled' | 'In Progress' | 'Completed' | 'Cancelled';
  createdAt?: string;
}

// Add endpoints for offerings/sessions
const OFFERINGS_SESSIONS_ENDPOINTS = {
  // Offerings
  CREATE_OFFERING: (courseId: number) => `/instructor/courses/${courseId}/offerings`,
  GET_OFFERINGS: (courseId: number) => `/instructor/courses/${courseId}/offerings`,
  UPDATE_OFFERING: (offeringId: number) => `/instructor/offerings/${offeringId}`,
  DELETE_OFFERING: (offeringId: number) => `/instructor/offerings/${offeringId}`,

  // Sessions
  CREATE_SESSION: (courseId: number) => `/instructor/courses/${courseId}/sessions`,
  GET_SESSIONS: (courseId: number) => `/instructor/courses/${courseId}/sessions`,
  UPDATE_SESSION: (sessionId: number) => `/instructor/sessions/${sessionId}`,
  DELETE_SESSION: (sessionId: number) => `/instructor/sessions/${sessionId}`,
};

/**
 * Create a course offering
 */
export const createCourseOffering = async (
  courseId: number,
  offeringData: CourseOffering
): Promise<{
  success: boolean;
  data?: { id: number; message: string };
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.post<ApiResponse<{ id: number; message: string }>>(
      OFFERINGS_SESSIONS_ENDPOINTS.CREATE_OFFERING(courseId),
      offeringData
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to create offering',
      },
    };
  }
};

/**
 * Get course offerings
 */
export const getCourseOfferings = async (courseId: number): Promise<{
  success: boolean;
  data?: CourseOffering[];
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.get<ApiResponse<CourseOffering[]>>(
      OFFERINGS_SESSIONS_ENDPOINTS.GET_OFFERINGS(courseId)
    );

    return {
      success: true,
      data: response.data || [],
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch offerings',
      },
    };
  }
};

/**
 * Update a course offering
 */
export const updateCourseOffering = async (
  offeringId: number,
  offeringData: Partial<CourseOffering>
): Promise<{
  success: boolean;
  data?: { message: string };
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.put<ApiResponse<{ message: string }>>(
      OFFERINGS_SESSIONS_ENDPOINTS.UPDATE_OFFERING(offeringId),
      offeringData
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to update offering',
      },
    };
  }
};

/**
 * Delete a course offering
 */
export const deleteCourseOffering = async (offeringId: number): Promise<{
  success: boolean;
  error?: { message: string };
}> => {
  try {
    await httpClient.delete<ApiResponse<null>>(
      OFFERINGS_SESSIONS_ENDPOINTS.DELETE_OFFERING(offeringId)
    );

    return {
      success: true,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to delete offering',
      },
    };
  }
};

/**
 * Create a course session
 */
export const createCourseSession = async (
  courseId: number,
  sessionData: CourseSession
): Promise<{
  success: boolean;
  data?: { id: number; message: string };
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.post<ApiResponse<{ id: number; message: string }>>(
      OFFERINGS_SESSIONS_ENDPOINTS.CREATE_SESSION(courseId),
      sessionData
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to create session',
      },
    };
  }
};

/**
 * Get course sessions
 */
export const getCourseSessions = async (
  courseId: number,
  offeringId?: number
): Promise<{
  success: boolean;
  data?: CourseSession[];
  error?: { message: string };
}> => {
  try {
    const url = offeringId
      ? `${OFFERINGS_SESSIONS_ENDPOINTS.GET_SESSIONS(courseId)}?offeringId=${offeringId}`
      : OFFERINGS_SESSIONS_ENDPOINTS.GET_SESSIONS(courseId);

    const response = await httpClient.get<ApiResponse<CourseSession[]>>(url);

    return {
      success: true,
      data: response.data || [],
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch sessions',
      },
    };
  }
};

/**
 * Update a course session
 */
export const updateCourseSession = async (
  sessionId: number,
  sessionData: Partial<CourseSession>
): Promise<{
  success: boolean;
  data?: { message: string };
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.put<ApiResponse<{ message: string }>>(
      OFFERINGS_SESSIONS_ENDPOINTS.UPDATE_SESSION(sessionId),
      sessionData
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to update session',
      },
    };
  }
};

/**
 * Delete a course session
 */
export const deleteCourseSession = async (sessionId: number): Promise<{
  success: boolean;
  error?: { message: string };
}> => {
  try {
    await httpClient.delete<ApiResponse<null>>(
      OFFERINGS_SESSIONS_ENDPOINTS.DELETE_SESSION(sessionId)
    );

    return {
      success: true,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to delete session',
      },
    };
  }
};

// ============================================================================
// COURSE STATUS UPDATE
// ============================================================================

/**
 * Update course status
 */
export const updateCourseStatus = async (
  courseId: number,
  status: 'active' | 'inactive' | 'pending'
): Promise<{
  success: boolean;
  data?: { courseId: number; status: string; message: string };
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.patch<ApiResponse<{ courseId: number; status: string; message: string }>>(
      `/instructor/courses/${courseId}/status`,
      { status }
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to update course status',
      },
    };
  }
};
